import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, CommentCard, Modal, ModalBody, ModalHeader, useToast } from '@vd/ui';
import { Maximize2 } from 'lucide-react';
import { api } from '../../lib/api';
import { commentTime } from '../../lib/When';
import { useAuth } from '../auth/AuthProvider';
import { MentionTextarea } from '../mentions/MentionTextarea';
import { MarkdownBody } from '../comments/MarkdownBody';
import type { User } from '@vd/types';
import styles from './CommentsSection.module.css';

export interface CommentsSectionProps {
  ticketId: string;
  members: { user: User; role: 'owner' | 'member' | 'viewer' }[];
}

export function CommentsSection({ ticketId, members }: CommentsSectionProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const [draft, setDraft] = useState('');
  // Expanded view: the same list + composer in a Modal so the user
  // can read long threads and compose without the surrounding ticket
  // detail page taking screen real-estate. State owned here so the
  // draft + edit handlers stay shared across both renderings.
  const [modalOpen, setModalOpen] = useState(false);

  const commentsQ = useQuery({
    queryKey: ['comments', ticketId],
    queryFn: () => api.listComments(ticketId),
  });

  const addMutation = useMutation({
    mutationFn: (body: string) => api.addComment(ticketId, body),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['comments', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      toast.push({ variant: 'success', title: 'Comment posted' });
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      api.updateComment(ticketId, commentId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comments', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      toast.push({ variant: 'success', title: 'Comment updated' });
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Edit failed', description: (err as Error).message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (commentId: string) => api.deleteComment(ticketId, commentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['comments', ticketId] });
      qc.invalidateQueries({ queryKey: ['ticket', ticketId] });
      toast.push({ variant: 'success', title: 'Comment deleted' });
    },
    onError: (err) => {
      toast.push({ variant: 'danger', title: 'Delete failed', description: (err as Error).message });
    },
  });

  const memberMap = new Map(members.map((m) => [m.user.id, m]));
  const memberUsers = useMemo(() => members.map((m) => m.user), [members]);
  const comments = commentsQ.data ?? [];

  // List + composer JSX, reused inline below and inside the expand
  // modal so the same data + handlers drive both renderings. We don't
  // factor this into a separate component because the parent owns all
  // the mutation hooks and draft state — extracting would mean
  // threading a dozen props for no real reuse benefit.
  const renderList = () => (
    comments.length === 0 ? (
      <div className={styles.empty}>
        <strong>No comments yet</strong>
        <span>Add one below, or tell Claude Code to work this ticket via MCP.</span>
      </div>
    ) : (
      <div className={styles.list}>
        {comments.map((c) => {
          const m = memberMap.get(c.authorId);
          // Only the author can edit/delete. The API enforces this too —
          // this flag just hides the UI affordances for non-authors.
          const canModify = !!user && c.authorId === user.id;
          return (
            <CommentCard
              key={c.id}
              comment={c}
              author={m?.user}
              authorRole={m?.role}
              formatTime={commentTime}
              canModify={canModify}
              onSaveEdit={canModify ? async (body) => { await editMutation.mutateAsync({ commentId: c.id, body }); } : undefined}
              onDelete={canModify ? async () => { await deleteMutation.mutateAsync(c.id); } : undefined}
              renderBody={(body) => <MarkdownBody body={body} members={memberUsers} />}
            />
          );
        })}
      </div>
    )
  );

  const renderComposer = (forModal = false) => (
    <div className={styles.composer}>
      {user && <Avatar name={user.name} size="sm" />}
      <div className={styles.composerBody}>
        <MentionTextarea
          className={styles.textarea}
          placeholder="Write a comment…  ⌘↵ to send"
          value={draft}
          onChange={setDraft}
          members={memberUsers}
          currentUserId={user?.id}
          // Focus the modal's textarea on open instead of letting the
          // Modal's auto-focus heuristic land on the first comment's
          // edit/delete button. Page-embedded composer doesn't get
          // autofocus — that would steal focus on initial page load.
          autoFocus={forModal}
          onKeyDown={(e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && draft.trim()) {
              // Stop the native event so the page-level Save & return
              // listener (TicketDetail.tsx, attached to window) doesn't
              // also fire. React's synthetic stopPropagation isn't enough
              // because React delegates events at its root and the native
              // event keeps bubbling past that to window listeners.
              e.nativeEvent.stopImmediatePropagation();
              addMutation.mutate(draft);
            }
          }}
        />
        <div className={styles.composerFooter}>
          <span className={styles.hint}>Markdown supported</span>
          <Button
            variant="primary"
            size="sm"
            disabled={!draft.trim()}
            loading={addMutation.isPending}
            onClick={() => addMutation.mutate(draft)}
          >
            Comment
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className={styles.label}>
        Comments <span className={styles.count}>{comments.length}</span>
        <button
          type="button"
          className={styles.expand}
          onClick={() => setModalOpen(true)}
          title="Open comments in a focused view"
        >
          <Maximize2 size={12} />
          Expand
        </button>
      </div>

      {renderList()}
      {renderComposer()}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} width={720}>
        <ModalHeader>Comments ({comments.length})</ModalHeader>
        <ModalBody>
          <div className={styles.modalScroll}>
            {renderList()}
          </div>
          {renderComposer(true)}
        </ModalBody>
      </Modal>
    </div>
  );
}
